import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Stack,
  Button,
} from "@mui/material";
import {
  People as PeopleIcon,
  Wifi as OnlineIcon,
  Pending as PendingIcon,
  PersonOff as NoEmployeeIcon,
  LocalCarWash as CarWashIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import statisticsService, { IRMStats } from "../../../services/statistics.service";
import { logger } from "../../../utils/logger";

const RMDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const adminData = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("adminData") || "{}");
    } catch {
      return {};
    }
  }, []);

  const [stats, setStats] = useState<IRMStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRMStats = async () => {
    try {
      setError("");
      const data = await statisticsService.getRMStats();
      setStats(data);
      logger.info("Fetched RM dashboard statistics");
    } catch (err: any) {
      setError(err.message || t("dashboard.failedToFetch"));
      logger.error("Failed to fetch RM statistics", err);
    } finally {
      setLoading(false);
    }
  };

  // Single combined effect: immediate fetch on mount + 30s live refresh.
  // Using one effect (not two) avoids the double API call React Strict Mode
  // triggers in dev when initial-fetch and interval are split across effects.
  useEffect(() => {
    fetchRMStats();
    const id = setInterval(fetchRMStats, 30000);
    return () => clearInterval(id);
    // Mount-only fetch + interval; fetchRMStats is a plain (non-memoized)
    // function recreated every render, so adding it here would tear down
    // and re-create the interval (and refetch) on every render instead of
    // once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !stats) {
    return (
      <Box className="p-4">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Box className="p-4">
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Hi, {adminData.firstName ?? ""} {adminData.lastName ?? ""}
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom mb={3}>
        {t("dashboard.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Pending rows — always first, full-width ── */}
      {(stats.owners.pending > 0 || stats.carWashes.pending > 0) && (
        <Stack spacing={1} sx={{ mb: 4 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="warning.dark" textTransform="uppercase" letterSpacing={0.5}>
            {t("dashboard.pendingActions")}
          </Typography>
          {stats.owners.pending > 0 && (
            <Alert
              severity="warning"
              sx={{ cursor: "pointer", "&:hover": { filter: "brightness(0.96)" }, alignItems: "center" }}
              onClick={() => navigate("/dashboard/manage-owners", { state: { filter: "pending" } })}
              action={
                <Button
                  size="small"
                  color="inherit"
                  endIcon={<ArrowForwardIcon />}
                  onClick={(e) => { e.stopPropagation(); navigate("/dashboard/manage-owners", { state: { filter: "pending" } }); }}
                >
                  {t("common.view")}
                </Button>
              }
            >
              <strong>{stats.owners.pending}</strong>&nbsp;{t("dashboard.pendingOwners")}
            </Alert>
          )}
          {stats.carWashes.pending > 0 && (
            <Alert
              severity="warning"
              sx={{ cursor: "pointer", "&:hover": { filter: "brightness(0.96)" }, alignItems: "center" }}
              onClick={() => navigate("/dashboard/manage-car-washes", { state: { filter: "pending" } })}
              action={
                <Button
                  size="small"
                  color="inherit"
                  endIcon={<ArrowForwardIcon />}
                  onClick={(e) => { e.stopPropagation(); navigate("/dashboard/manage-car-washes", { state: { filter: "pending" } }); }}
                >
                  {t("common.view")}
                </Button>
              }
            >
              <strong>{stats.carWashes.pending}</strong>&nbsp;{t("dashboard.pendingCarWashes")}
            </Alert>
          )}
        </Stack>
      )}

      {/* Your Owners */}
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {t("dashboard.owners")}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, md: 6, lg: 3 }}>
          <Card
            sx={{ height: "100%", cursor: "pointer" }}
            onClick={() => navigate("/dashboard/manage-owners")}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t("dashboard.total")}
                </Typography>
                <PeopleIcon color="primary" sx={{ fontSize: 36 }} />
              </Box>
              <Typography variant="h3" fontWeight="bold">
                {stats.owners.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, md: 6, lg: 3 }}>
          {/* Deep-links to Manage Owners with the Active filter (owner online or has
              an online employee) — the union of both numbers on this card. */}
          <Card
            sx={{ height: "100%", cursor: "pointer" }}
            onClick={() => navigate("/dashboard/manage-owners", { state: { filter: "active" } })}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t("owners.statusOnline")}
                </Typography>
                <OnlineIcon color="success" sx={{ fontSize: 36 }} />
              </Box>
              <Box display="flex" gap={3}>
                <Box>
                  <Typography variant="h3" fontWeight="bold" color="success.main">
                    {stats.owners.online}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("dashboard.onlineOwners")}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight="bold" color="success.main">
                    {stats.owners.onlineEmployees}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("dashboard.onlineEmployees")}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, md: 6, lg: 3 }}>
          <Card
            sx={{ height: "100%", cursor: "pointer" }}
            onClick={() => navigate("/dashboard/manage-owners", { state: { filter: "pending" } })}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t("dashboard.pending")}
                </Typography>
                <PendingIcon color="warning" sx={{ fontSize: 36 }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" color="warning.main">
                {stats.owners.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, md: 6, lg: 3 }}>
          <Card
            sx={{ height: "100%", cursor: "pointer" }}
            onClick={() => navigate("/dashboard/manage-owners", { state: { filter: "no-employee" } })}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t("owners.withoutEmployees")}
                </Typography>
                <NoEmployeeIcon color="error" sx={{ fontSize: 36 }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" color="error.main">
                {stats.owners.withoutEmployee}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Your Car Washes */}
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {t("dashboard.carWashes")}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 6, lg: 3 }}>
          <Card
            sx={{ height: "100%", cursor: "pointer" }}
            onClick={() => navigate("/dashboard/manage-car-washes")}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t("dashboard.total")}
                </Typography>
                <CarWashIcon color="info" sx={{ fontSize: 36 }} />
              </Box>
              <Typography variant="h3" fontWeight="bold">
                {stats.carWashes.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, md: 6, lg: 3 }}>
          <Card
            sx={{ height: "100%", cursor: "pointer" }}
            onClick={() => navigate("/dashboard/manage-car-washes", { state: { filter: "pending" } })}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t("dashboard.pending")}
                </Typography>
                <PendingIcon color="warning" sx={{ fontSize: 36 }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" color="warning.main">
                {stats.carWashes.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RMDashboard;
